import React, { useState, useEffect } from 'react'
import { Box, SimpleGrid, TextInput, Flex, Group, Button, Select, NumberInput, Text, Paper, Checkbox, Progress, Popover, MultiSelect, Loader } from '@mantine/core'
import { getAllCountryList, getAllProvinceList, getAllCityList, getAllDistrictList } from '../../../services/location'
import { notificationSuccess, notificationError } from '../../ui/Notifications'
import { addSupplier, updateSupplier } from '../../../services/supplier'
import UniqueRequired from '../../ui/UniqueRequired'
import { SupplierFormProvider } from './hook/supplier-form'
import { useFormSupplier } from './schema/form'
import FormLogoSupplier from './section/FormLogoSupplier'
import FormBiographySupplier from './section/FormBiographySupplier'
import FormRefundPolicySupplier from './section/FormRefundPolicySupplier'
import FormWarrantyPolicySupplier from './section/FormWarrantyPolicySupplier'
import FormPurchaseNoteSupplier from './section/FormPurchaseNoteSupplier'
import UserListSupplier from './section/UserListSupplier'
import { useDebouncedCallback } from '@mantine/hooks'

const defaultTypeDiscount = [
  {
    value: 'percentage',
    label: 'Percentage Discount'
  },
  {
    value: 'fixed',
    label: 'Fixed Discount'
  },
]

const requirements = [
  { 
    re: /[0-9]/, 
    label: 'Includes number'
  },
  { 
    re: /[a-z]/, 
    label: 'Includes lowercase letter' 
  },
  { 
    re: /[$&+,:;=?@#|'<>.^*()%!-]/, 
    label: 'Includes special symbol' 
  },
]

function getStrength(form) {
  let multiplier = form.length > 0 ||  form === '' ? 0 : 1

  requirements.forEach((requirement) => {
    if (!requirement.re.test(form)) {
      multiplier += 1
    }
  })

  return Math.max(100 - (100 / (requirements.length + 1)) * multiplier, 10)
}


const FormSupplier = ({ dataSupplier, onCloseModal, reloadList }) => {
  const form = useFormSupplier()
  const [loadingForm, setLoadingForm] = useState(false)
  const [countryList, setCountryList] = useState([])
  const [provinceList, setProvinceList] = useState([])
  const [cityList, setCityList] = useState([])
  const [districtList, setDistrictList] = useState([])
  const [currencyList, setCurrencyList] = useState([])
  const [popoverAcronimName, setPopoverAcronimName] = useState(false)
  const [popoverInvitationCode, setPopoverInvitationCode] = useState(false)
  const [loadingDiscountCity, setLoadingDiscountCity] = useState(false)
  const [discountCityList, setDiscountCityList] = useState([])
  const [selectedDiscountCity, setSelectedDiscountCity] = useState([])
  const [searchDiscountCity, setSearchDiscountCity] = useState('')

  const checkAcronimName = requirements.map((val, index) => (
    <UniqueRequired key={index} label={val.label} meets={val.re.test(form.getValues().acronim_name)} />
  ))
  const strengthAcronimName = getStrength(form.getValues().acronim_name)

  const progressAcronimName = Array(3).fill(0).map((_, index) => {
    return (
      <Progress
        styles={{ section: { transitionDuration: '0ms' }}}
        value={Object.values(form.getValues().acronim_name).length > 0 && index === 0 ? 100 : strengthAcronimName >= ((index + 1) / 3) * 100 ? 100 : 0}
        color={strengthAcronimName > 60 ? 'teal' : strengthAcronimName > 50 ? 'yellow' : 'red'}
        key={index}
        size={3}
      />
    )
  })

  const checkInvitationCode = requirements.map((val, index) => (
    <UniqueRequired key={index} label={val.label} meets={val.re.test(form.getValues().invitation_code)} />
  ))

  const strengthInvitationCode = getStrength(form.getValues().invitation_code)

  const progressInvitationCode = Array(3).fill(0).map((_, index) => {
    return (
      <Progress
        styles={{ section: { transitionDuration: '0ms' }}}
        value={Object.values(form.getValues().invitation_code).length > 0 && index === 0 ? 100 : strengthInvitationCode >= ((index + 1) / 3) * 100 ? 100 : 0}
        color={strengthInvitationCode > 80 ? 'teal' : strengthInvitationCode > 50 ? 'yellow' : 'red'}
        key={index}
        size={3}
      />
    )
  })

  const handleSubmitSupplier = async (form) => {
    setLoadingForm(true)
    let methodFunction = null
    let titleMessageSuccess = ''
    let captionMessageSuccess = ''
    let titleMessageError = ''
    let captionMessageError = ''
    if (dataSupplier !== null) {
      methodFunction = updateSupplier(dataSupplier.id, form)
      titleMessageSuccess = 'Update Supplier Successfully'
      captionMessageSuccess = 'You have been updated supplier'
      titleMessageError = 'Update Supplier Error'
    } else {
      methodFunction = addSupplier(form)
      titleMessageSuccess = 'Add Supplier Successfully'
      captionMessageSuccess = 'You have been added new supplier'
      titleMessageError = 'Add Supplier Error'
    }
    try {
      const response = await methodFunction
      if (response.success) {
        onCloseModal()
        reloadList()
        notificationSuccess(titleMessageSuccess, captionMessageSuccess)
      }
    } catch (error) {
      console.log(error)
      const errorMessage = error.response.data.message
      captionMessageError = Object.keys(errorMessage) ? errorMessage : 'Please recheck your form'
      notificationError(titleMessageError, captionMessageError)
    } finally {
      setLoadingForm(false)
    }
  }

  const handlingScrolledError = (error) => {
    const errorField = Object.keys(error)[0]
    if (errorField) {
      const inputNode = form.getInputNode(errorField)
      if (inputNode) {
        inputNode.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }

  const handleGetCountryList = async () => {
    const params = {
      'page[num]': '1',
      'page[limit]': '99999',
    }
    try {
      const response = await getAllCountryList(params)
      const dataCountry = response.data
      const remapDataCountry = dataCountry.map((val) => {
        return {
          label: val.attributes.name,
          value: `${val.attributes.id}`
        }
      })
      setCountryList(remapDataCountry)

      // eslint-disable-next-line
      const remapDataCurrency = dataCountry.map((val) => {
        return val.attributes.currency ? val.attributes.currency : null
      })

      const filterNullValue = remapDataCurrency.filter((val) => val !== null)
      const filterDuplicateCurrency = filterNullValue.reduce((result, item) => {
        const removed = result.filter((val) => val !== item)
        return [...removed, item]
      }, [])

      setCurrencyList(filterDuplicateCurrency)
    } catch (error) {
      console.log(error)
    }
  }

  const handleGetProvinceList = async (countryId) => {
    const params = {
      'page[num]': '1',
      'page[limit]': '99999',
      'filter[countries.id][is]': countryId,
      sort: '-id',
    }
    try {
      const response = await getAllProvinceList(params)
      const dataProvince = response.data
      const remapDataProvince = dataProvince.map((val) => {
        return {
          label: val.attributes.name,
          value: `${val.attributes.id}`
        }
      })
      setProvinceList(remapDataProvince)
    } catch (error) {
      console.log(error)
    }
  }

  const handleGetCityList = async (countryId, provinceId) => {
    const params = {
      'page[num]': '1',
      'page[limit]': '99999',
      'filter[countries.id][is]': countryId,
      'filter[provinces.id][is]': provinceId,
      sort: '-id',
    }
    try {
      const response = await getAllCityList(params)
      const dataCity = response.data
      const remapDataCity = dataCity.map((val) => {
        return {
          label: val.attributes.name,
          value: `${val.attributes.id}`
        }
      })
      setCityList(remapDataCity)
    } catch (error) {
      console.log(error)
    }
  }

  const handleGetDistrictList = async (countryId, provinceId, cityId) => {
    const params = {
      'page[num]': '1',
      'page[limit]': '99999',
      'filter[countries.id][is]': countryId,
      'filter[provinces.id][is]': provinceId,
      'filter[cities.id][is]': cityId,
      sort: '-id',
    }
    try {
      const response = await getAllDistrictList(params)
      const dataDistrict = response.data
      const remapDistrictCity = dataDistrict.map((val) => {
        return {
          label: val.attributes.name,
          value: `${val.attributes.id}`
        }
      })
      setDistrictList(remapDistrictCity)
    } catch (error) {
      console.log(error)
    }
  }

  const handleGetCityForDiscountList = useDebouncedCallback(async () => {
    setLoadingDiscountCity(true)
    const params = {
      'page[num]': '1',
      'page[limit]': '20',
      'filter[name][like]': searchDiscountCity,
      sort: '-id',
    }
    try {
      const response = await getAllCityList(params)
      const dataCity = response.data

      let dataDiscountCity = []
      const remapDataCity = dataCity.map((val) => {
        return {
          label: val.attributes.name,
          value: `${val.attributes.id}`
        }
      })

      if (selectedDiscountCity.length > 0) {
        const joinData = remapDataCity.concat(selectedDiscountCity)
        const findDuplicate = joinData.reduce((result, item) => {
          const removed = result.filter((val) => val.value !== item.value)
          return [...removed, item]
        }, [])
        dataDiscountCity = findDuplicate
      } else {
        dataDiscountCity = remapDataCity
      }

      setDiscountCityList(dataDiscountCity)
    } catch (error) {
      console.log(error)
    } finally {
      if (discountCityList) {
        setLoadingDiscountCity(false)
      }
    }
  }, 500)

  const handleSetForm = (detail) => {
    const findCurrency = (data) => {
      let currency =  null
      const dataCurrency = data.find((val) => val.key === 'min_order_currency')
      if (dataCurrency) {
        currency = dataCurrency.value
      }
      return currency
    }

    const findMinimumOrder = (data) => {
      let minimumOrder =  0
      const dataMinimumOrder = data.find((val) => val.key === 'min_order_value')
      if (dataMinimumOrder) {
        minimumOrder = Number(dataMinimumOrder.value)
      }
      return minimumOrder
    }

    if (detail.discount_cities.length > 0) {
      setSelectedDiscountCity(detail.discount_cities)
    }

    const setDataDiscountCity = (dataCity) => {
      const remapDataCity = dataCity.map((val) => {
        return `${val.value}`
      })

      return remapDataCity
    }

    const detailData = {
      name: detail.name,
      country_id: `${detail.country_id}`,
      province_id: detail.province_id ? `${detail.province_id}` : null,
      city_id: detail.city_id ? `${detail.city_id}` : null,
      district_id: detail.district_id ? `${detail.district_id}` : null,
      address: detail.address,
      website: detail.website,
      acronim_name: detail.acronim_name ? detail.acronim_name : '',
      invitation_code: detail.invitation_code ? detail.invitation_code : '',
      group_link: detail.group_link,
      slug: detail.slug,
      pic_name: detail.pic_details[0].value,
      pic_phone: detail.pic_details[1].value,
      pic_email: detail.pic_details[2].value,
      active: detail.active,
      featured: detail.featured,
      biography: detail.biography ? detail.biography : '',
      refund_policy: detail.refund_policy ? detail.refund_policy : '',
      warranty_policy: detail.warranty_policy ? detail.warranty_policy : '',
      purchase_note: detail.purchase_note ? detail.purchase_note : '',
      currency: detail.settings.length > 0 ? findCurrency(detail.settings) : 'IDR',
      minimum_order: detail.settings.length > 0 ? findMinimumOrder(detail.settings) : 0,
      users: detail.users,
      is_enable_discount: detail.is_enable_discount,
      discount_minimum_spend: detail.discount_minimum_spend || 0,
      discount_amount: detail.discount_amount || 0,
      discount_type: detail.discount_type,
      discount_cities: detail.discount_cities.length > 0 ? setDataDiscountCity(detail.discount_cities) : [],
      logo_encoded: detail.logo ? detail.logo : null,
    }

    form.initialize(detailData)
    form.setValues(detailData)
  }

  useEffect(() => {
    handleGetCountryList()
    if (dataSupplier) {
      handleSetForm(dataSupplier)
    }
    // eslint-disable-next-line
  }, [dataSupplier])

  useEffect(() => {
    handleGetCityForDiscountList()
    // eslint-disable-next-line
  }, [searchDiscountCity])

  useEffect(() => {
    const selectedCountry = form.getValues().country_id
    const selectedProvince = form.getValues().province_id
    const selectedCity = form.getValues().city_id
    if (selectedCountry) {
      handleGetProvinceList(selectedCountry)
    }
    if (selectedProvince) {
      handleGetCityList(selectedCountry, selectedProvince)
    }
    if (selectedCity) {
      handleGetDistrictList(selectedCountry, selectedProvince, selectedCity)
    }
    // eslint-disable-next-line
  }, [form.getValues().country_id, form.getValues().province_id, form.getValues().city_id])

  form.watch('country_id', ({ previousValue, value }) => {
    if (previousValue !== value) {
      form.setValues((prev) => ({ ...prev, province_id: null, city_id: null, district_id: null }))
      setProvinceList([])
      setCityList([])
      setDistrictList([])
    }
    if (value === null) {
      setProvinceList([])
      setCityList([])
      setDistrictList([])
      form.setValues((prev) => ({ ...prev, province_id: null, city_id: null, district_id: null }))
    }
  })

  form.watch('province_id', ({ previousValue, value }) => {
    if (previousValue !== value) {
      form.setValues((prev) => ({ ...prev, city_id: null, district_id: null }))
      setCityList([])
      setDistrictList([])
    }
    if (value === null) {
      setCityList([])
      setDistrictList([])
      form.setValues((prev) => ({ ...prev, city_id: null, district_id: null }))
    }
  })

  form.watch('city_id', ({ previousValue, value }) => {
    if (previousValue !== value) {
      form.setValues((prev) => ({ ...prev, district_id: null }))
      setDistrictList([])
    }
    if (value === null) {
      setDistrictList([])
      form.setValues((prev) => ({ ...prev, district_id: null }))
    }
  })

  const handleChangeFormNoSpace = (name, val) => {
    form.setValues((prev) => ({...prev, [name]: val.trim()}))
  }

  const loadingDataTemplate = () => {
    return (
      <Box ta='center' my={12}>
        <Loader size={16} />
        <Text c='gray.6' fz={11}>Loading Data</Text>
      </Box>
    )
  }


  const mappingFormDiscount = (enableDiscount) => {
    const handleSelectDiscountCity = (idCity) => {
      const findCity = discountCityList.find((val) => val.value === idCity)
      if (findCity) {
        setSelectedDiscountCity(oldVal => [...oldVal, findCity])
        form.insertListItem('discount_cities', idCity)
      }
    }

    const handleRemoveDiscountCity = (idCity) => {
      const filteredCity = selectedDiscountCity.filter((val) => val.value !== idCity)
      setSelectedDiscountCity(filteredCity)
      form.removeListItem('discount_cities', idCity)
    }

    if (enableDiscount) {
      return (
        <Box>
          <SimpleGrid cols={2} verticalSpacing="sm" mb='md'>
            <NumberInput
              name='discount_minimum_spend'
              label="Discount Minimum Spend"
              leftSection={<Text fz='xs' c='black' tt='capitalize'>{form.getValues().currency ? form.getValues().currency : '???'}</Text>}
              key={form.key('discount_minimum_spend')}
              {...form.getInputProps('discount_minimum_spend')}
              size='xs'
              withAsterisk
              thousandSeparator=","
              leftSectionWidth={40}
            />
          </SimpleGrid>
          <SimpleGrid cols={2} verticalSpacing="sm" mb='md'>
            <Select
              name='discount_type'
              label='Discount Type'
              data={defaultTypeDiscount}
              key={form.key('discount_type')}
              {...form.getInputProps('discount_type')}
              size='xs'
              allowDeselect={false}
            />
            <NumberInput
              name='discount_amount'
              label="Discount Amount"
              leftSection={form.getValues().discount_type === 'fixed' ? <Text fz='xs' c='black' tt='capitalize'>{form.getValues().currency ? form.getValues().currency : '???'}</Text> : ''}
              leftSectionWidth={form.getValues().discount_type === 'fixed' ? 40 : 0}
              rightSection={form.getValues().discount_type === 'percentage' ? <Text fz='xs' c='black' tt='capitalize'>%</Text> : ''}
              rightSectionWidth={form.getValues().discount_type === 'percentage' ? 40 : 0}
              key={form.key('discount_amount')}
              {...form.getInputProps('discount_amount')}
              size='xs'
              withAsterisk
              thousandSeparator=","
            />
            <MultiSelect
              name='discount_cities'
              label="Discount For Cities"
              size='xs'
              data={discountCityList}
              {...form.getInputProps('discount_cities')}
              onOptionSubmit={(val) => handleSelectDiscountCity(val)}
              onRemove={(val) => handleRemoveDiscountCity(val)}
              searchable
              searchValue={searchDiscountCity}
              onSearchChange={setSearchDiscountCity}
              hidePickedOptions
              nothingFoundMessage={!loadingDiscountCity ? discountCityList.length > 0 ? '' : 'No City Found' : loadingDataTemplate()}
              withAsterisk
            />
          </SimpleGrid>
        </Box>
      )
    }
  }

  return (
    <Box>
      <SupplierFormProvider form={form}>
        <Paper shadow="xs" radius="md" withBorder p="sm" mb='md'>
          <Text fz={14} fw='bold' mb='sm'>Supplier Information</Text>
          <SimpleGrid cols={2} verticalSpacing="sm" mb='md'>
            <Box>
              <TextInput
                name='name'
                label='Supplier Name'
                key={form.key('name')}
                {...form.getInputProps('name')}
                withAsterisk
                size='xs'
              />
            </Box>
            <Box>
              <Select
                name='country_id'
                label='Country Supplier'
                placeholder='Select Country'
                data={countryList}
                key={form.key('country_id')}
                {...form.getInputProps('country_id')}
                size='xs'
                searchable
                nothingFoundMessage="No Country Found"
                clearable
                withAsterisk
                allowDeselect={false}
                onClear={() => form.setValues((prev) => ({ ...prev, province_id: null, city_id: null, district_id: null }))}
              />
            </Box>
            <Box>
              <Select
                name='province_id'
                label='Province Supplier'
                placeholder='Select Province'
                data={provinceList}
                key={form.key('province_id')}
                {...form.getInputProps('province_id')}
                size='xs'
                searchable
                nothingFoundMessage="No Province Found"
                clearable
                allowDeselect={false}
                disabled={form.getValues().country_id === null}
                onClear={() => form.setValues((prev) => ({ ...prev, city_id: null, district_id: null }))}
              />
            </Box>
            <Box>
              <Select
                name='city_id'
                label='City Supplier'
                placeholder='Select City'
                data={cityList}
                key={form.key('city_id')}
                {...form.getInputProps('city_id')}
                size='xs'
                searchable
                nothingFoundMessage="No City Found"
                clearable
                allowDeselect={false}
                disabled={form.getValues().country_id === null || form.getValues().province_id === null}
                onClear={() => form.setValues((prev) => ({ ...prev, district_id: null }))}
              />
            </Box>
            <Box>
              <Select
                name='district_id'
                label='Subdistrict Supplier'
                placeholder='Select Subdistrict'
                data={districtList}
                key={form.key('district_id')}
                {...form.getInputProps('district_id')}
                size='xs'
                searchable
                nothingFoundMessage="No Subdistrict Found"
                clearable
                allowDeselect={false}
                disabled={form.getValues().country_id === null || form.getValues().province_id === null || form.getValues().city_id === null}
              />
            </Box>
            <Box>
              <TextInput
                name='address'
                label='Supplier Address'
                key={form.key('address')}
                {...form.getInputProps('address')}
                size='xs'
              />
            </Box>
            <Box>
              <TextInput
                name='website'
                label='Website'
                key={form.key('website')}
                {...form.getInputProps('website')}
                withAsterisk
                size='xs'
              />
            </Box>
            <Box>
              <Popover opened={popoverAcronimName} position="bottom" width="target" transitionProps={{ transition: 'pop' }}>
                <Popover.Target>
                  <Box onFocusCapture={() => setPopoverAcronimName(true)} onBlurCapture={() => setPopoverAcronimName(false)}>
                    <TextInput
                      name='acronim_name'
                      label='Supplier Acronym Name'
                      key={form.key('acronim_name')}
                      {...form.getInputProps('acronim_name')}
                      size='xs'
                    />
                  </Box>
                </Popover.Target>
                <Popover.Dropdown>
                  <Group gap={5} grow mt="xs" mb="md">
                    {progressAcronimName}
                  </Group>
                  {checkAcronimName}
                </Popover.Dropdown>
              </Popover>
            </Box>
            <Box>
              <Popover opened={popoverInvitationCode} position="bottom" width="target" transitionProps={{ transition: 'pop' }}>
                <Popover.Target>
                  <Box onFocusCapture={() => setPopoverInvitationCode(true)} onBlurCapture={() => setPopoverInvitationCode(false)}>
                    <TextInput
                      name='invitation_code'
                      {...form.getInputProps('invitation_code')}
                      label='Supplier Invitation Code'
                      onChange={(val) => handleChangeFormNoSpace('invitation_code', val.target.value)}
                      size='xs'
                    />
                  </Box>
                </Popover.Target>
                <Popover.Dropdown>
                  <Group gap={5} grow mt="xs" mb="md">
                    {progressInvitationCode}
                  </Group>
                  {checkInvitationCode}
                </Popover.Dropdown>
              </Popover>
            </Box>
            <Box>
              <TextInput
                name='group_link'
                label='Group Support Invitation Link'
                key={form.key('group_link')}
                {...form.getInputProps('group_link')}
                size='xs'
              />
            </Box>
            <Box>
              <Select
                name='currency'
                label='Currency'
                placeholder='Select Currency'
                data={currencyList}
                key={form.key('currency')}
                {...form.getInputProps('currency')}
                size='xs'
                searchable
                nothingFoundMessage="No Currency Found"
                clearable
                withAsterisk
                allowDeselect={false}
              />
            </Box>
            <Box>
              <NumberInput
                name='minimum_order'
                label="Minimum Order"
                leftSection={<Text fz='xs' c='black' tt='capitalize'>{form.getValues().currency ? form.getValues().currency : '???'}</Text>}
                placeholder='Input minimum order'
                key={form.key('minimum_order')}
                {...form.getInputProps('minimum_order')}
                size='xs'
                withAsterisk
                thousandSeparator=","
                leftSectionWidth={40}
              />
            </Box>
            <Box>
              <TextInput
                name='slug'
                label='Slug Supplier'
                placeholder='Input slug supplier'
                key={form.key('slug')}
                {...form.getInputProps('slug')}
                withAsterisk
                size='xs'
              />
            </Box>
          </SimpleGrid>
          <Checkbox
            mb='sm'
            size='xs'
            label="Active Supplier"
            key={form.key('active')}
            {...form.getInputProps('active', { type: 'checkbox' })}
          />
          <Checkbox
            mb='sm'
            size='xs'
            label="Featured Supplier"
            key={form.key('featured')}
            {...form.getInputProps('featured', { type: 'checkbox' })}
          />
          <Checkbox
            mb='sm'
            size='xs'
            label="Enable Courier Cost Discount"
            key={form.key('is_enable_discount')}
            {...form.getInputProps('is_enable_discount', { type: 'checkbox' })}
          />
          {mappingFormDiscount(form.getValues().is_enable_discount)}
        </Paper>

        <FormLogoSupplier />

        <FormBiographySupplier />

        <Paper shadow="xs" radius="md" withBorder p="sm" mb='md'>
          <Text fz={14} fw='bold' mb='sm'>PIC Details</Text>
          <SimpleGrid cols={3} verticalSpacing="sm" mb='md'>
            <Box>
              <TextInput
                name='pic_name'
                label='Name'
                key={form.key('pic_name')}
                {...form.getInputProps('pic_name')}
                size='xs'
              />
            </Box>
            <Box>
              <TextInput
                name='pic_email'
                label='Email'
                key={form.key('pic_email')}
                {...form.getInputProps('pic_email')}
                size='xs'
              />
            </Box>
            <Box>
              <TextInput
                name='pic_phone'
                label='Phone'
                key={form.key('pic_phone')}
                {...form.getInputProps('pic_phone')}
                size='xs'
              />
            </Box>
          </SimpleGrid>
        </Paper>

        <FormRefundPolicySupplier />

        <FormWarrantyPolicySupplier />

        <FormPurchaseNoteSupplier />

        <UserListSupplier />

        <Box mt={20}>
          <Flex justify='flex-end'>
            <Group>
              <Button size='xs' variant="outline" onClick={onCloseModal}>Close</Button>
              <Button
                size='xs'
                loading={loadingForm}
                variant="filled"
                onClick={form.onSubmit((value) => { handleSubmitSupplier(value) }, (error) => { handlingScrolledError(error) })}
              >
                {dataSupplier !== null ? 'Update' : 'Create'}
              </Button>
            </Group>
          </Flex>
        </Box>
      </SupplierFormProvider>
    </Box>
  )
}

export default FormSupplier